/**
 * Session/identity + capability provider — the SINGLE seam between the client auth layer and the
 * server's "who am I + what may I do" surface.
 *
 * SERVER STATE: the JWT carries no capabilities (F-02); the effective set is resolved server-side
 * per request. The `iam` module exposes the live identity + capabilities surface (GET /iam/me +
 * GET /iam/me/capabilities — server/src/modules/iam/routes.js). The graceful-degrade fallback below
 * (501/404 → empty-capability session) is retained as defense for a degraded backend. The contract:
 *
 *   GET  {SESSION_ENDPOINT}        -> { utilisateur: CurrentUser, laboratoire: LabRef }
 *   GET  {CAPABILITIES_ENDPOINT}   -> { capabilities: string[] }   // the effective set
 *
 * Both return the standard { success, data } envelope. Until those endpoints exist, the provider
 * degrades gracefully: a logged-in session with an empty capability set (every gated screen shows
 * ForbiddenState rather than crashing), which is the correct deny-by-default posture.
 */
import { api, ApiError } from "@/api";

/** Flip these to the real paths when the iam identity surface lands. */
export const SESSION_ENDPOINT = "/iam/me";
export const CAPABILITIES_ENDPOINT = "/iam/me/capabilities";

export interface CurrentUser {
  id: string;
  email: string;
  nomComplet: string;
  laboratoireId: string;
  estProprietaire: boolean;
  presets: string[];
  /** Cross-lab platform super-admin (est_admin_plateforme) — the signal the management console keys off. */
  estAdminPlateforme?: boolean;
}

export interface LabRef {
  id: string;
  nom: string;
}

export interface SessionBundle {
  user: CurrentUser;
  lab: LabRef | null;
  capabilities: string[];
}

interface MeResponse {
  utilisateur: CurrentUser;
  laboratoire: LabRef | null;
}
interface CapabilitiesResponse {
  capabilities: string[];
}

/**
 * Fetch the live identity + effective capability set. Tolerant of the not-yet-implemented
 * endpoints (501 / 404) so the scaffold runs end-to-end today: returns an empty-capability
 * session keyed off the decoded access-token claims rather than failing the login.
 */
export async function fetchSession(fallbackClaims?: AccessTokenClaims): Promise<SessionBundle> {
  const [me, caps] = await Promise.allSettled([
    api.get<MeResponse>(SESSION_ENDPOINT),
    api.get<CapabilitiesResponse>(CAPABILITIES_ENDPOINT),
  ]);

  if (me.status === "fulfilled") {
    return {
      user: me.value.utilisateur,
      lab: me.value.laboratoire,
      capabilities: caps.status === "fulfilled" ? caps.value.capabilities : [],
    };
  }

  // Endpoint not implemented yet (501) or missing (404) — degrade to token claims, empty caps.
  if (me.reason instanceof ApiError && [501, 404].includes(me.reason.httpStatus) && fallbackClaims) {
    return {
      user: {
        id: fallbackClaims.utilisateurId,
        email: "",
        nomComplet: "",
        laboratoireId: fallbackClaims.laboratoireId,
        estProprietaire: false,
        presets: [],
      },
      lab: null,
      capabilities: [],
    };
  }
  throw me.reason;
}

/** Thin access-token claim shape (server signs { utilisateurId, laboratoireId, tokenVersion, aud }). */
export interface AccessTokenClaims {
  utilisateurId: string;
  laboratoireId: string;
  tokenVersion?: number;
  aud?: string;
  exp?: number;
}

/** Decode a JWT payload WITHOUT verifying (display/claims only — never a trust boundary). */
export function decodeAccessClaims(token: string | null): AccessTokenClaims | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const json = atob(parts[1]!.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as Record<string, unknown>;
    const utilisateurId = claims.utilisateurId ?? claims.sub;
    const laboratoireId = claims.laboratoireId;
    if (utilisateurId == null || laboratoireId == null) return null;
    return {
      utilisateurId: String(utilisateurId),
      laboratoireId: String(laboratoireId),
      ...(claims.tokenVersion != null ? { tokenVersion: Number(claims.tokenVersion) } : {}),
      ...(claims.aud != null ? { aud: String(claims.aud) } : {}),
      ...(claims.exp != null ? { exp: Number(claims.exp) } : {}),
    };
  } catch {
    return null;
  }
}
